import { GoogleLogin } from '@react-oauth/google';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { Capacitor } from '@capacitor/core';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import Header from '../components/Header';

export default function Login() {
    const router = useRouter();
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            GoogleSignIn.initialize({
                clientId: '724616878464-g68ec6lpo5cbogf69hcmus36s3orvf5s.apps.googleusercontent.com',
            }).catch(err => console.error('Google Sign-In initialization failed', err));
        }
    }, []);

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const res = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/google`, {
                credential: credentialResponse.credential,
                client_id: credentialResponse.clientId,
            });

            dispatch({ type: 'SET_USER', payload: res.data.user });
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(res.data.user));

            router.push('/');
        } catch (error) {
            setError(error.response?.data?.message || 'Google login failed');
        }
    };

    const handleNativeGoogleLogin = async () => {
        try {
            const result = await GoogleSignIn.signIn();

            // The plugin can return idToken in different structures depending on version
            const idToken = result?.authentication?.idToken || result?.idToken;

            if (!idToken) {
                setError('Google Sign-In failed: No ID Token found.');
                return;
            }

            const res = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/google`, {
                credential: idToken,
                client_id: '724616878464-g68ec6lpo5cbogf69hcmus36s3orvf5s.apps.googleusercontent.com',
            });

            dispatch({ type: 'SET_USER', payload: res.data.user });
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(res.data.user));

            router.push('/');
        } catch (error) {
            console.error('Native Google Login Error:', error);
            setError('Native Google login failed');
        }
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/login`, { email, password });
            dispatch({ type: 'SET_USER', payload: res.data.user });
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(res.data.user));
            router.push('/');
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <Header />
            <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-md space-y-8 bg-white p-10 rounded-2xl shadow-xl border border-gray-100">
                    <div className="text-center">
                        <h2 className="text-3xl font-extrabold text-gray-900">Sign In</h2>
                        <p className="mt-2 text-sm text-gray-600">Access your Easytravel account</p>
                    </div>

                    {error && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center border border-red-100">
                            {error}
                        </div>
                    )}

                    <form className="mt-8 space-y-6" onSubmit={handleEmailLogin}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full border-2 border-gray-100 focus:border-green-500 rounded-xl px-4 py-3 text-sm transition-all outline-none"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Password</label>
                                <input
                                    type="password"
                                    required
                                    className="w-full border-2 border-gray-100 focus:border-green-500 rounded-xl px-4 py-3 text-sm transition-all outline-none"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-green-600/20"
                        >
                            Log In
                        </button>
                    </form>

                    <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500 uppercase tracking-wider text-xs font-bold">Or continue with</span>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        {Capacitor.isNativePlatform() ? (
                            <button
                                onClick={handleNativeGoogleLogin}
                                className="flex items-center justify-center gap-3 w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold py-3 rounded-xl transition-all shadow-md"
                            >
                                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-5 w-5" alt="Google" />
                                Sign in with Google
                            </button>
                        ) : (
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => setError('Google login failed')}
                            />
                        )}
                    </div>

                    <p className="mt-8 text-center text-sm text-gray-600">
                        Don't have an account? <Link href="/register" className="font-bold text-green-600 hover:text-green-700 transition-colors">Register here</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
